export {Maker as Page};

import {useState, useEffect, useRef, useCallback} from 'preact/hooks';
import {PlayCircleIcon, PauseCircleIcon, InformationCircleIcon, EyeIcon} from '@heroicons/react/24/solid';
import {Helmet} from "react-helmet-async";
import messages from "../../../messages/messages.js";
import API_URL from "../../../includes/api.js";
import {AccountSidebar} from "../../../components/AccountSidebar.jsx";
import {ContentLocked} from "../../../components/ContentLocked.jsx";
import {useAuth} from '../../../components/AuthProvider.jsx';
import {CircleSpinner} from "../../../components/Spinner.jsx";


// APP COMPONENT: AudioPreview
function AudioPreview({audioName, isPlaying, onPlayPause, playbackSpeed, isLoading, audioDuration}) {
    const [currentTime, setCurrentTime] = useState(0);
    const animationFrameRef = useRef(null);
    const lastUpdateTimeRef = useRef(0);
    const accumulatedTimeRef = useRef(0);

    useEffect(() => {
        if (!audioName || !isPlaying || audioDuration <= 0) {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            return;
        }

        lastUpdateTimeRef.current = performance.now();

        const animate = (timestamp) => {
            const deltaTime = (timestamp - lastUpdateTimeRef.current) / 1000;
            lastUpdateTimeRef.current = timestamp;

            const currentEffectiveSpeed = Math.max(0.01, playbackSpeed);
            accumulatedTimeRef.current += deltaTime * currentEffectiveSpeed;

            if (audioDuration > 0 && accumulatedTimeRef.current >= (audioDuration / currentEffectiveSpeed)) {
                accumulatedTimeRef.current %= (audioDuration / currentEffectiveSpeed);
            }
            setCurrentTime(accumulatedTimeRef.current);
            animationFrameRef.current = requestAnimationFrame(animate);
        };
        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [isPlaying, audioName, audioDuration, playbackSpeed]);

    useEffect(() => {
        setCurrentTime(0);
        accumulatedTimeRef.current = 0;
    }, [audioDuration, audioName]);


    const formatTime = (timeInSeconds) => {
        const validTime = Math.max(0, timeInSeconds);
        const minutes = Math.floor(validTime / 60);
        const seconds = Math.floor(validTime % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds.toFixed(0)}`;
    };

    const currentSpeedForDisplay = playbackSpeed > 0 ? playbackSpeed : 1;
    const displayDuration = audioDuration > 0 ? audioDuration / currentSpeedForDisplay : 0;
    const progressValue = displayDuration > 0 ? (currentTime / displayDuration) * 100 : 0;


    return (
        <>
            {audioName && <h3 className="text-xl mt-0 mb-4"><b className="text-red-400">3.</b> Listen to
                preview</h3>}
            {audioName && <div className="text-sm">Now playing: {audioName} (Looping)</div>}
            <div>
                {isLoading ? (
                    <div className="flex items-center">
                        <CircleSpinner/>
                        <h3 className="text-lg m-0">Loading...</h3>
                    </div>
                ) : audioName && audioDuration > 0 && (
                    <div className="bg-zinc-900 rounded-lg p-4 mb-4 shadow flex flex-col items-center">
                        <progress
                            value={progressValue > 100 ? 100 : progressValue}
                            max="100"
                            aria-label="Audio progress"
                            className="w-full h-3 rounded-lg overflow-hidden accent-red-400 bg-zinc-800"
                        />
                        <div className="flex my-2 justify-between text-sm">
                            <span>{formatTime(currentTime)}</span>/
                            <span>{formatTime(displayDuration)}</span>
                        </div>
                        <div className="text-center">
                            <button onClick={onPlayPause} className="cursor-pointer"
                                    aria-label={isPlaying ? 'Pause audio' : 'Play audio'}>
                                {isPlaying ? <PauseCircleIcon className="h-14 w-14"/> :
                                    <PlayCircleIcon className="h-14 w-14"/>}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}


// Helper function to convert AudioBuffer to WAV Blob
function audioBufferToWav(buffer) {
    const numOfChan = buffer.numberOfChannels;
    const length = buffer.length * numOfChan * 2 + 44;
    const ab = new ArrayBuffer(length);
    const view = new DataView(ab);
    const channels = [];
    let i;
    let sample;
    let offset = 0;
    let pos = 0;

    function setUint16(data) {
        view.setUint16(pos, data, true);
        pos += 2;
    }

    function setUint32(data) {
        view.setUint32(pos, data, true);
        pos += 4;
    }

    setUint32(0x46464952); // "RIFF"
    setUint32(length - 8); // file length - 8
    setUint32(0x45564157); // "WAVE"

    setUint32(0x20746d66); // "fmt " chunk
    setUint32(16); // length of format data
    setUint16(1); // type of format (1 = PCM)
    setUint16(numOfChan);
    setUint32(buffer.sampleRate);
    setUint32(buffer.sampleRate * 2 * numOfChan); // byte rate
    setUint16(numOfChan * 2); // block align
    setUint16(16); // bits per sample

    setUint32(0x61746164); // "data" chunk
    setUint32(length - pos - 4); // data length

    for (i = 0; i < numOfChan; i++) {
        channels.push(buffer.getChannelData(i));
    }

    while (pos < length - 44) {
        for (i = 0; i < numOfChan; i++) {
            if (offset < channels[i].length) {
                sample = Math.max(-1, Math.min(1, channels[i][offset]));
                sample = (0.5 + sample < 0 ? sample * 32768 : sample * 32767) | 0;
                view.setInt16(pos, sample, true);
            } else {
                view.setInt16(pos, 0, true);
            }
            pos += 2;
        }
        offset++;
    }
    return new Blob([view], {type: 'audio/wav'});
}

const DEFAULT_SPEED = parseFloat(messages['MAKER_SPEED'], 1);
const DEFAULT_PITCH = parseFloat(messages['DEFAULT_PITCH'], 0);

function Maker() {
    const [audioName, setAudioName] = useState(null);
    const [pageLoading, setPageLoading] = useState(true);
    const [allowed, setAllowed] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isProcessingDownload, setIsProcessingDownload] = useState(false);
    const [audioBufferDuration, setAudioBufferDuration] = useState(0);
    const [playbackSpeed, setPlaybackSpeed] = useState(DEFAULT_SPEED);
    const [pitchShift, setPitchShift] = useState(DEFAULT_PITCH);
    const [notification, setNotification] = useState(null);
    const audioContextRef = useRef(null);
    const audioBufferRef = useRef(null);
    const sourceNodeRef = useRef(null);

    const downloadDisabled = !audioName || isUploading;
    const effectivePlaybackRate = playbackSpeed * (2 ** (pitchShift / 12));

    const {getToken, user, authLoading} = useAuth();

    useEffect(() => {
        if (authLoading) return;

        if (!user) return;

        getToken().then(token => {
            fetch(API_URL + "/website/get?firebase_auth_token=" + token)
                .then(response => response.json()).then(data => {
                if (data.code === 200) {
                    setAllowed(true);
                } else if (data.code === 201) {
                    setAllowed(true);
                }
            }).finally(() => {
                setPageLoading(false);
            });
        });
    }, [authLoading, user])

    const cleanupAudio = useCallback(() => {
        if (sourceNodeRef.current) {
            sourceNodeRef.current.onended = null;
            try {
                sourceNodeRef.current.stop();
            } catch (e) {
            }
            sourceNodeRef.current.disconnect();
            sourceNodeRef.current = null;
        }
        setIsPlaying(false);
    }, []);

    const playAudio = useCallback(() => {
        if (!audioContextRef.current || !audioBufferRef.current || !audioName) return;

        cleanupAudio();

        const audioCtx = audioContextRef.current;
        const source = audioCtx.createBufferSource();
        source.buffer = audioBufferRef.current;

        source.playbackRate.value = effectivePlaybackRate;
        source.loop = true;

        source.connect(audioCtx.destination);
        source.start(0);
        sourceNodeRef.current = source;
        setIsPlaying(true);
    }, [audioName, effectivePlaybackRate, cleanupAudio]);

    const handleAudioLoad = async (file, name) => {
        cleanupAudio();
        setIsUploading(true);
        setAudioName(null);
        audioBufferRef.current = null;
        setAudioBufferDuration(0);
        setNotification(null);

        try {
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            const audioCtx = audioContextRef.current;

            const arrayBuffer = await file.arrayBuffer();

            audioBufferRef.current = await audioCtx.decodeAudioData(arrayBuffer);
            setAudioName(name);
            setAudioBufferDuration(audioBufferRef.current.duration);
            playAudio();
        } catch (error) {
            setNotification({
                type: "error",
                message: "Audio Load Error: Could not load or decode the audio file. Please try a different file.",
            });
            setAudioName(null);
            audioBufferRef.current = null;
        } finally {
            setIsUploading(false);
        }
    };

    const handlePlayPause = () => {
        if (!audioBufferRef.current || !audioName) {
            setNotification({
                type: "error",
                message: "No Audio: Please load an audio file to play.",
            });
            return;
        }

        if (isPlaying) {
            cleanupAudio();
        } else {
            if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume().then(playAudio);
            } else {
                playAudio();
            }
        }
    };

    useEffect(() => {
        if (isPlaying && sourceNodeRef.current && audioContextRef.current) {
            if (audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume();
            }
            sourceNodeRef.current.playbackRate.setValueAtTime(effectivePlaybackRate, audioContextRef.current.currentTime);
        }
    }, [effectivePlaybackRate, isPlaying]);

    const handleDownload = async () => {
        if (!audioBufferRef.current || !audioName) {
            setNotification({
                type: "error",
                message: "No Audio Loaded: Please upload an audio file before downloading.",
            });
            return;
        }
        if (effectivePlaybackRate <= 0) {
            setNotification({
                type: "error",
                message: "Invalid Speed: Playback speed must be greater than zero to download.",
            });
            return;
        }

        setIsProcessingDownload(true);
        setNotification(null);
        try {
            const originalBuffer = audioBufferRef.current;

            const outputDuration = originalBuffer.duration / effectivePlaybackRate;
            const outputLength = Math.ceil(outputDuration * originalBuffer.sampleRate);

            if (outputLength <= 0 || isNaN(outputLength) || !isFinite(outputLength)) {
                setNotification({
                    type: "error",
                    message: "Processing Error: Cannot process audio with zero, negative, or invalid duration. Check speed/pitch settings.",
                });
                setIsProcessingDownload(false);
                return;
            }

            const offlineCtx = new OfflineAudioContext(
                originalBuffer.numberOfChannels,
                outputLength,
                originalBuffer.sampleRate
            );

            const offlineSource = offlineCtx.createBufferSource();
            offlineSource.buffer = originalBuffer;
            offlineSource.playbackRate.value = effectivePlaybackRate;

            offlineSource.connect(offlineCtx.destination);
            offlineSource.start(0);

            const renderedBuffer = await offlineCtx.startRendering();
            const wavBlob = audioBufferToWav(renderedBuffer);

            const url = URL.createObjectURL(wavBlob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `edited_${audioName.split('.')[0] || 'audio'}.wav`;
            document.body.appendChild(a);
            a.target = '_blank';
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            setNotification({
                type: "success",
                message: `Download Started: Your edited version of ${audioName} is downloading as WAV.`,
            });

        } catch (error) {
            setNotification({
                type: "error",
                message: "Download Error: Could not process or download the audio. Settings might result in an invalid audio length or another processing issue. Check console for details.",
            });
        } finally {
            setIsProcessingDownload(false);
        }
    };

    const handleDownloadClick = () => {
        if (downloadDisabled && !isProcessingDownload) {
            setNotification({
                type: "error",
                message: "Cannot Download: Please load an audio file first.",
            });
            return;
        }
        handleDownload().then(r => {
        });
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile) {
            if (!['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp3', 'audio/x-m4a', 'audio/aac', 'audio/flac'].includes(selectedFile.type)) {
                setNotification({
                    type: "error",
                    message: "Invalid File Type: Please upload MP3, WAV, OGG, M4A, AAC or FLAC files.",
                });
                setFile(null);
                event.target.value = '';
                return;
            }
            setNotification(null); // Clear previous notifications
            handleAudioLoad(selectedFile, selectedFile.name).then(r => {
            });
        }
    };

    useEffect(() => {
        return () => {
            cleanupAudio();
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close().catch(e => console.warn("Error closing audio context:", e));
            }
        };
    }, [cleanupAudio]);

    return (
        <section className="text-white">

            <Helmet>
                <title>{messages["SITE.NAME"]} | Make your own {messages["SITE.NAME"]} music!</title>
                <meta name="description"
                      content={`Convert your favorite song into a ${messages["SITE.NAME"]} version!`}/>
            </Helmet>

            <div className="flex flex-wrap">

                <AccountSidebar/>

                {pageLoading ? (
                    <div className="w-full md:w-3/4 px-0 md:px-4">
                        <div className="p-6 text-white rounded-lg bg-zinc-800">
                            <div className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                     xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                            strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor"
                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                </svg>
                                <h3 className="text-lg m-0">Loading...</h3>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="w-full md:w-3/4 px-0 md:px-4">

                        {!allowed && (
                            <div className="p-4 mb-4 text-yellow-800 rounded-lg bg-yellow-50">
                                <div className="flex items-center">
                                    <InformationCircleIcon className="w-5 h-5 mr-2"/>
                                    <h3 className="text-lg m-0 text-yellow-800 leading-none">Content partially
                                        locked</h3>
                                </div>
                                <div className="text-sm mt-2 mb-2">
                                    Download is locked because you are not a subscriber. Become a subscriber to download
                                    the edited audio.
                                </div>
                                <a href='/subscribe'
                                   className="text-white bg-yellow-800 hover:bg-yellow-900 font-medium rounded-lg text-xs px-3 py-1.5 text-center inline-flex items-center">
                                    <EyeIcon className="w-4 h-4 mr-2"/>
                                    Subscribe
                                </a>
                            </div>
                        )}

                        <div className="border border-border/60 bg-zinc-800 rounded-lg p-6 mb-4">

                            {notification && (
                                <div
                                    className={
                                        "p-4 mb-4 text-sm rounded-lg " +
                                        (notification.type === 'error'
                                            ? "text-red-800 bg-red-50"
                                            : "text-green-800 bg-green-50")
                                    }
                                    role={notification.type === 'error' ? 'alert' : 'status'}
                                >
                                    <span className="font-medium"> {notification.message}</span>
                                </div>
                            )}

                            <h3 className="text-2xl mt-0 mb-2">{messages["MAKER.NAME"]}</h3>
                            <p className="mb-4 text-sm text-gray-400">Convert your favorite song into
                                a {messages["SITE.NAME"]} version!</p>

                            <div className="w-full max-w-md space-y-4">
                                <div className="mb-4">
                                    <h3 className="text-xl mt-0 mb-4"><b className="text-red-400">1.</b> Select
                                        a song</h3>
                                    <input
                                        id="audio-file"
                                        type="file"
                                        accept=".mp3,.wav,.ogg,.m4a,.aac,.flac"
                                        onChange={handleFileChange}
                                        className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 mb-2"
                                    />
                                    <p className="mt-1 text-xs text-gray-300">MP3, WAV, OGG, M4A, AAC, FLAC (MAX.
                                        10MB).</p>
                                </div>

                                {audioName && (
                                    <>
                                        <h3 className="text-xl mt-0 mb-4"><b className="text-red-400">2.</b> Select
                                            options</h3>

                                        <div className="mb-2">Speed: <b className="text-red-400">{playbackSpeed}</b>x
                                        </div>
                                        <input type="range" value={playbackSpeed}
                                               onChange={(e) => setPlaybackSpeed(e.target.value)}
                                               min="0.1" max="2"
                                               step="0.05"
                                               className="w-full accent-red-400"/>

                                        <div className="mb-2">Pitch Shift: <b
                                            className="text-red-400">{pitchShift}</b> semitones
                                        </div>
                                        <input type="range" value={pitchShift}
                                               onChange={(e) => setPitchShift(e.target.value)}
                                               min="-12" max="12"
                                               step="1"
                                               className="w-full accent-red-400"/>
                                    </>
                                )}

                                <AudioPreview
                                    audioName={audioName}
                                    isPlaying={isPlaying}
                                    onPlayPause={handlePlayPause}
                                    playbackSpeed={effectivePlaybackRate}
                                    isLoading={isUploading}
                                    audioDuration={audioBufferDuration}
                                />

                                {audioName && allowed && (
                                    <button
                                        onClick={handleDownloadClick}
                                        className="text-white cursor-pointer border border-white hover:bg-white hover:text-black font-medium rounded-lg text-sm px-10 py-2.5 text-center"
                                        disabled={downloadDisabled || isProcessingDownload}
                                    >
                                        {isProcessingDownload ? 'Processing...' : 'Download Edited Audio'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
